"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useSpecialties } from "@/lib/swr";
import { specialtyLabel } from "@/lib/specialties/search";
import { groupLabel, GROUP_ORDER } from "@/lib/specialties/groups";
import type { SpecialtyOption } from "@/lib/specialties/types";
import { Button } from '@/components/ui/button';

interface SearchSectionProps {
  onSearch?: (query: string, specialty: string, type: string) => void;
  activeFilter?: string;
  includePast?: boolean;
  onIncludePastChange?: (value: boolean) => void;
}

export default function SearchSection({ onSearch, activeFilter = 'all', includePast = false, onIncludePastChange }: SearchSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [selectedType, setSelectedType] = useState(activeFilter);
  const { data: specialtyRows } = useSpecialties();
  const [showFilters, setShowFilters] = useState(false);
  const { t, language } = useLanguage();
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const desktopFilterButtonRef = useRef<HTMLButtonElement>(null);
  const mobileFilterButtonRef = useRef<HTMLButtonElement>(null);

  // Only rows tied to a МОЗ specialty leaf can be used to filter events –
  // events are matched against target_specialties by that name.
  const specialtiesByGroup = useMemo(() => {
    const rows = (specialtyRows ?? []).filter((row): row is SpecialtyOption & { mozLeaf: string } => row.mozLeaf !== null);
    return GROUP_ORDER
      .map(key => ({ key, items: rows.filter(row => row.groupKey === key) }))
      .filter(group => group.items.length > 0);
  }, [specialtyRows]);

  useEffect(() => {
    setSelectedType(activeFilter);
  }, [activeFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showFilters) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking any filter button or inside either dropdown
      const filterButtons = [desktopFilterButtonRef.current, mobileFilterButtonRef.current];
      const dropdowns = [desktopDropdownRef.current, mobileDropdownRef.current];

      const isInsideButton = filterButtons.some((button) => button ? button.contains(target) : false);
      const isInsideDropdown = dropdowns.some((dropdown) => dropdown ? dropdown.contains(target) : false);

      if (!isInsideButton && !isInsideDropdown) {
        setShowFilters(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showFilters]);

  const typeOptions = [
    { value: 'all', label: t('home.search.types.all') },
    { value: 'event', label: t('home.search.types.events') },
    { value: 'course', label: t('home.search.types.courses') }
  ];

  // Calculate active filter count
  const activeFilterCount = [
    selectedType !== 'all',
    selectedSpecialty !== 'all',
    includePast
  ].filter(Boolean).length;

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchQuery, selectedSpecialty, selectedType);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
      setShowFilters(false);
    }
  };

  const clearFilters = () => {
    setSelectedType('all');
    setSelectedSpecialty('all');
    if (onIncludePastChange) onIncludePastChange(false);
    // Filters will auto-apply via useEffect
  };

  // Auto-apply filters when type or specialty changes
  useEffect(() => {
    // Only trigger if we have a callback and filters have been changed from initial values
    if (onSearch) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedSpecialty]);

  const getActiveFilterLabels = () => {
    const labels = [];
    if (selectedType !== 'all') {
      labels.push(typeOptions.find(opt => opt.value === selectedType)?.label);
    }
    if (selectedSpecialty !== 'all') {
      const specialty = (specialtyRows ?? []).find(row => row.id === selectedSpecialty);
      if (specialty) {
        labels.push(specialtyLabel(specialty, language));
      }
    }
    if (includePast) {
      labels.push(t('home.search.showPastEvents'));
    }
    return labels;
  };

  return (
    <div className="w-full relative">
      {/* Main Search Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200/50 overflow-visible backdrop-blur-sm relative z-10">

        {/* Desktop Layout */}
        <div className="hidden sm:block">
          <div className="flex items-center p-2 gap-2">
            {/* Search Input */}
            <div className="flex-1 flex items-center gap-3 px-4 py-2">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('home.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 text-base outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded placeholder:text-gray-400 bg-transparent"
              />
            </div>

            {/* Filters Button */}
            <div className="relative">
              <Button
                ref={desktopFilterButtonRef}
                variant={showFilters || activeFilterCount > 0 ? 'primary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
                className={`relative flex items-center justify-center min-w-[44px] min-h-[44px] px-3 py-3 rounded-lg font-medium transition-all ${
                  showFilters || activeFilterCount > 0
                    ? 'shadow-sm'
                    : 'bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-blue-600 border border-blue-200">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Dropdown Panel */}
              {showFilters && (
                <div
                  ref={desktopDropdownRef}
                  className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">{t('home.search.filters')}</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors h-auto"
                      aria-label="Close filters"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>

                  {/* Filter Options */}
                  <div className="p-4 space-y-4">
                    {/* Type Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t('home.search.type')}
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {typeOptions.map((option) => (
                          <Button
                            key={option.value}
                            variant={selectedType === option.value ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedType(option.value)}
                            className={`px-2 py-2 text-xs font-medium rounded-md transition-colors min-h-[44px] text-center whitespace-normal leading-tight ${
                              selectedType === option.value
                                ? ''
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Specialty Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t('home.search.specialty')}
                      </label>
                      <div className="relative">
                        <select
                          value={selectedSpecialty}
                          onChange={(e) => setSelectedSpecialty(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white hover:border-gray-300 outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors appearance-none cursor-pointer pr-8"
                        >
                          <option value="all">{t('home.search.specialties.all')}</option>
                          {specialtiesByGroup.map(group => (
                            <optgroup key={group.key} label={groupLabel(group.key, language)}>
                              {group.items.map((specialty) => (
                                <option key={specialty.id} value={specialty.id}>
                                  {specialtyLabel(specialty, language)}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
                          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end px-4 py-3 bg-white border-t border-gray-100">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={clearFilters}
                      className="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors h-auto p-0"
                    >
                      {t('home.search.clearFilters')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Search Button */}
            <Button
              onClick={handleSearch}
              className="px-6 py-3 font-semibold rounded-lg transition-colors whitespace-nowrap"
            >
              {t('home.search.button')}
            </Button>
          </div>

          {/* Active Filters Display */}
          {activeFilterCount > 0 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {getActiveFilterLabels().map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full border border-blue-200/50"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Layout */}
        <div className="sm:hidden">
          {/* Search Input Row */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-3 py-2">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('home.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 text-base focus:outline-none placeholder:text-gray-400 bg-transparent min-h-[44px]"
              />
            </div>
          </div>

          {/* Buttons Row */}
          <div className="flex items-center gap-2 p-2">
            {/* Filters Button - Mobile */}
            <div className="relative flex-1">
              <Button
                ref={mobileFilterButtonRef}
                variant={showFilters || activeFilterCount > 0 ? 'primary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
                className={`relative flex items-center justify-center gap-2 w-full min-h-[44px] px-4 py-2.5 rounded-lg transition-all font-medium ${
                  showFilters || activeFilterCount > 0
                    ? 'shadow-sm'
                    : 'bg-white hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm text-gray-700'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                <span className="text-sm">{t('home.search.filters')}</span>
                {activeFilterCount > 0 && (
                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-blue-600 border border-blue-200">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Mobile Dropdown */}
              {showFilters && (
                <div
                  ref={mobileDropdownRef}
                  className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 max-h-[70vh] overflow-y-auto"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">{t('home.search.filters')}</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center h-auto"
                      aria-label="Close filters"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>

                  {/* Filter Options - Mobile */}
                  <div className="p-4 space-y-4">
                    {/* Type Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t('home.search.type')}
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {typeOptions.map((option) => (
                          <Button
                            key={option.value}
                            variant={selectedType === option.value ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedType(option.value)}
                            className={`px-2 py-2 text-xs font-medium rounded-md transition-colors min-h-[44px] text-center whitespace-normal leading-tight ${
                              selectedType === option.value
                                ? ''
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Specialty Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t('home.search.specialty')}
                      </label>
                      <div className="relative">
                        <select
                          value={selectedSpecialty}
                          onChange={(e) => setSelectedSpecialty(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm bg-white hover:border-gray-300 outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors appearance-none cursor-pointer min-h-[44px] pr-10"
                        >
                          <option value="all">{t('home.search.specialties.all')}</option>
                          {specialtiesByGroup.map(group => (
                            <optgroup key={group.key} label={groupLabel(group.key, language)}>
                              {group.items.map((specialty) => (
                                <option key={specialty.id} value={specialty.id}>
                                  {specialtyLabel(specialty, language)}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end px-4 py-3 bg-white border-t border-gray-100">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={clearFilters}
                      className="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors min-h-[44px] h-auto p-0"
                    >
                      {t('home.search.clearFilters')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Search Button - Mobile */}
            <Button
              onClick={handleSearch}
              className="flex-shrink-0 px-5 py-2.5 font-semibold rounded-lg transition-colors min-h-[44px] flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </Button>
          </div>

          {/* Active Filters Display - Mobile */}
          {activeFilterCount > 0 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {getActiveFilterLabels().map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full border border-blue-200/50"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Show past events toggle */}
      {onIncludePastChange && (
        <div className="mt-3 flex justify-end">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => onIncludePastChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-600">{t('home.search.showPastEvents')}</span>
          </label>
        </div>
      )}
    </div>
  );
}
